import { Button } from 'primereact/button';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import { Messages } from 'primereact/messages';
import { ReactElement, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import {
  CartItemInput,
  SetFreePaymentMethodOnCartAndPlaceOrderDocument,
  SetStripePaymentMethodOnCartAndPlaceOrderDocument,
  useAddProductsToCartMutation,
  useRemoveItemFromCartMutation,
} from 'graphql/generated/magentoApi';
import { useCartContext } from 'CartContext';
import { useCartId, useZeroCartId } from 'hooks/magentoHooks';
import { useClient } from 'urql';
import { InputText } from 'primereact/inputtext';

export interface CheckoutPaypalProps {
  setOrderNumber: (orderNumber: string) => void;
}

export default function CheckoutPayment({
  setOrderNumber,
}: CheckoutPaypalProps): ReactElement {
  const { cartId } = useCartId();
  const { userInfo, billingAddress, total, items } = useCartContext();
  const client = useClient();
  const messages = useRef<Messages>(null);
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [zipCode, setZipCode] = useState(billingAddress?.postalCode);

  // To allow free  paid products in one order
  const { zeroCartId } = useZeroCartId();
  const [{ fetching: removeLoading, error: removeError }, removeItem] =
    useRemoveItemFromCartMutation();
  const [{ fetching: addLoading, error: addError }, addProducts] =
    useAddProductsToCartMutation();

  if (!userInfo || !billingAddress) {
    return <></>; // should never happen
  }

  const handleGraphqlResponse = async (response: any) => {
    const orderNumber = response.data?.placeOrder?.order?.order_number;
    if (orderNumber) {
      setLoading(false);
      setOrderNumber(orderNumber);
    } else if (response.error) {
      showError(response.error);
    } else {
      showStripeError();
    }
  };

  const showError = (error: any) => {
    setLoading(false);
    messages.current?.show({
      sticky: true,
      severity: 'error',
      detail: error.message || error,
    });
  };

  const showStripeError = () => showError('Unable to initiate Stripe');

  const nonZeroTotalWithFreeProducts = async () => {
    const freeProducts = items.filter(
      item => item.product.price_range.maximum_price.final_price.value === 0,
    );
    // eslint-disable-next-line no-restricted-syntax
    for await (const item of freeProducts) {
      removeItem({
        input: {
          cart_id: cartId,
          cart_item_uid: item.uid,
        },
      });
    }
    const itemsForFree = freeProducts.map(item => ({
      sku: item.product.sku,
      quantity: 1,
    }));
    // eslint-disable-next-line no-restricted-syntax
    await addProducts({
      cartId: zeroCartId,
      cartItems: itemsForFree as CartItemInput[],
    });
    if (!(removeLoading && addLoading && removeError && addError)) {
      await client
        .mutation(SetFreePaymentMethodOnCartAndPlaceOrderDocument, {
          cartId: zeroCartId,
        })
        .toPromise();
      ReactGA.event({
        category: 'ecommerce',
        action: 'Checkout Successful',
        label: 'Free Checkout Complete',
      });
    }
  };

  const handleStripeSubmit = async () => {
    setLoading(true);

    const areZeroProducts = items.some(
      item => item.product.price_range.maximum_price.final_price.value === 0,
    );
    if (areZeroProducts) {
      await nonZeroTotalWithFreeProducts();
      setLoading(true);
    }

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      showStripeError();
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);

    if (cardNumberElement) {
      const { paymentMethod: payment, error } =
        await stripe.createPaymentMethod({
          type: 'card',
          card: cardNumberElement,
          billing_details: {
            address: {
              postal_code: zipCode,
            },
          },
        });
      if (payment) {
        const variables = {
          cartId,
          token: payment.id,
        };

        const response = await client
          .mutation(
            SetStripePaymentMethodOnCartAndPlaceOrderDocument,
            variables,
          )
          .toPromise();
        handleGraphqlResponse(response);
      } else if (error) {
        console.log('[error]', error);
        showError(error);
      }
      return;
    }
    showStripeError();
    ReactGA.event({
      category: 'ecommerce',
      action: 'checkout_step_4',
      label: 'Stripe Checkout Complete',
    });
  };

  const handleFreeSubmit = async () => {
    setLoading(true);
    const response = await client
      .mutation(SetFreePaymentMethodOnCartAndPlaceOrderDocument, { cartId })
      .toPromise();
    handleGraphqlResponse(response);
    ReactGA.event({
      category: 'ecommerce',
      action: 'Checkout Successful',
      label: 'Free Checkout Complete',
    });
  };

  const isDark = localStorage.getItem('isDark') === 'true';

  const ELEMENT_OPTIONS = {
    showIcon: true,
    style: {
      base: {
        color: isDark ? 'rgba(255, 255, 255, 0.87)' : '#495057',
      },
    },
  };

  return (
    <div>
      <Messages ref={messages} />
      {Number(total) ? (
        <div>
          <h2 className="form-heading mb-2">Credit Card</h2>

          <div className="grid mb-4">
            <div className="flex flex-column w-full col-12">
              <label htmlFor="cardNumber" className="pl-2  text-sm">
                Card Number
              </label>
              <div className="p-2 mb-0 border-400 border-solid border-1 border-round">
                <CardNumberElement id="cardNumber" options={ELEMENT_OPTIONS} />
              </div>
            </div>
            <div className="flex flex-column col-6">
              <label htmlFor="expiry" className="p-0 pl-2 text-sm">
                Expiration
              </label>
              <div className="p-2 mb-0 border-400 border-solid border-1 border-round w-full">
                <CardExpiryElement id="expiry" options={ELEMENT_OPTIONS} />
              </div>
            </div>

            <div className="flex flex-column col-6">
              <label htmlFor="cvc" className="pl-2 text-sm">
                CVC
              </label>
              <div className="p-2 mb-0 border-400 border-solid border-1 border-round w-full">
                <CardCvcElement id="cvc" options={ELEMENT_OPTIONS} />
              </div>
            </div>
            <div className="flex flex-column col-6">
              <label htmlFor="postal" className="pl-2 text-sm">
                ZIP
              </label>
              <InputText
                id="postal"
                value={zipCode}
                onChange={e => setZipCode(e.target.value)}
                className="p-2 mb-0 border-400 border-solid border-1 border-round w-full"
              />
            </div>
          </div>
          <Button
            className="uppercase border-noround c-button-primary w-full will-change"
            onClick={handleStripeSubmit}
            loading={loading}
            label="Pay"
            type="button"
            id="checkout-payment"
          />
        </div>
      ) : (
        <div>
          <h2 className="form-heading">Your total is $0.00</h2>
          <Button
            className="uppercase border-noround c-button-primary w-full will-change"
            onClick={handleFreeSubmit}
            loading={loading}
            label="Confirm order"
            type="button"
            id="checkout-confirm"
          />
        </div>
      )}
    </div>
  );
}
